import {html} from "lit";
import {UiHelper} from "./ui.helper";

/**
 * The browser pieces the image preview needs. Injectable so the logic runs in this
 * repo's `node` test environment, which has neither `document` nor
 * `MutationObserver`; in the app, {@link browserEnvironment} supplies them.
 */
/** What the helper needs to know of the opened popover. */
export interface PreviewPopover {
    readonly isConnected: boolean;
}

export interface ImagePreviewEnvironment {
    /** Opens (or with `null` closes) the one global popover — `UiHelper.popover`. */
    popover(content: unknown, cssClasses: Record<string, boolean> | null): PreviewPopover | null;
    /**
     * Makes the opened preview a labelled modal dialog for assistive technology, moves
     * the keyboard focus into it and returns a function that gives the focus back to
     * where it was.
     */
    focusPreview(popover: PreviewPopover, label: string): () => void;
    /**
     * Where the key listener is registered — `document`. It closes the preview on Esc,
     * Enter and Space and holds Tab (the focus trap).
     */
    keyTarget: EventTarget;
    /** Calls `onMutation` whenever nodes are added to or removed from the document. */
    observe(onMutation: () => void): { disconnect(): void };
}

/** The real environment. Touches `document` only when called, never at module load. */
function browserEnvironment(): ImagePreviewEnvironment {
    return {
        popover: (content, cssClasses) => UiHelper.popover(content, cssClasses),
        focusPreview: (popover, label) => {
            const previous = document.activeElement;
            if (popover instanceof HTMLElement) {
                popover.setAttribute("role", "dialog");
                popover.setAttribute("aria-modal", "true");
                popover.setAttribute("aria-label", label);
                popover.tabIndex = -1;
                popover.focus({preventScroll: true});
            }
            return () => {
                if (previous instanceof HTMLElement && previous.isConnected) {
                    previous.focus({preventScroll: true});
                }
            };
        },
        keyTarget: document,
        observe: (onMutation) => {
            const observer = new MutationObserver(onMutation);
            // Child lists only: removals are all this is waiting for. Attribute changes
            // would call back on every style or class change in a busy timeline.
            observer.observe(document.body, {childList: true, subtree: true});
            return observer;
        },
    };
}

/** Keys that close the preview while it has the focus. */
const CLOSING_KEYS = new Set(["Escape", "Enter", " "]);

/** An element that is a link a click would follow. Duck-typed: `node` tests have no `Element`. */
function isLink(target: EventTarget): boolean {
    const candidate = target as { tagName?: unknown; hasAttribute?: (name: string) => boolean };
    return candidate.tagName === "A" && typeof candidate.hasAttribute === "function" && candidate.hasAttribute("href");
}

/**
 * Enlarged preview of an image in a rich text (Anke, 2026-09-26). Opened by
 * `ImagePreviewListener` for `<o2d-rich-text-editor>` (a click in view mode, a double
 * click in edit mode) and by the Quill
 * `ImageBlot` for legacy descriptions — one implementation for both.
 *
 * It reuses the preview the Quill editor used to open: `UiHelper.popover` with the
 * `full-size-preview` class, styled in dialogs.css as a full-window overlay. It closes
 *  - on a click anywhere on the overlay (the handler sits on `.img-preview`, which
 *    fills it — no `document` click listener, so the opening click cannot close it).
 *    The second click of a double click does not close it either;
 *  - on Esc, Enter or Space. The overlay takes the keyboard focus while it is open, as
 *    it takes the mouse: otherwise the focus stays on the element below — a clicked
 *    knowledge tile, say — and Enter would open that tile's dialog under the preview
 *    (review 0047/01). Tab and Shift+Tab are held, so the focus cannot wander to an
 *    element hidden under the overlay either (review 0047/02); the preview has nothing
 *    focusable but itself. It is announced as a modal dialog. Closing gives the focus
 *    back;
 *  - when the element it was opened from (`owner`) leaves the document, e.g. because
 *    the dialog around it was closed programmatically.
 *
 * `UiHelper.popover` is a singleton that other code (timeline hovers) replaces without
 * telling anyone. Both the key listener (Esc/Enter/Space, Tab trap) and the observer
 * therefore check first whether their preview is still the open one; if it was
 * replaced they unregister and close nothing — never somebody else's popover.
 */
export class ImagePreviewHelper {

    /** Unregisters the listeners of the preview opened last, if they are still active. */
    private static teardownActive: (() => void) | null = null;

    /** Whether the click path runs through a link — then the link wins, no preview. */
    public static isInLink(path: readonly EventTarget[]): boolean {
        return path.some(isLink);
    }

    public static open(src: string, alt: string, owner: { readonly isConnected: boolean }, env: ImagePreviewEnvironment = browserEnvironment()): void {
        ImagePreviewHelper.teardownActive?.();

        let observer: { disconnect(): void } | null = null;
        let popover: PreviewPopover | null = null;
        let restoreFocus: (() => void) | null = null;

        const teardown = (): void => {
            env.keyTarget.removeEventListener("keydown", onKeyDown);
            observer?.disconnect();
            observer = null;
            if (ImagePreviewHelper.teardownActive === teardown) {
                ImagePreviewHelper.teardownActive = null;
            }
        };
        const close = (): void => {
            teardown();
            if (popover?.isConnected) {
                env.popover(null, null);
                restoreFocus?.();
            }
        };
        const onClick = (event: MouseEvent): void => {
            // The second click of a double click on an image in a read view lands on
            // the preview its first click has just opened; it must not close it again
            // (review 0048/01). Only that one click is skipped: a third quick click
            // (`detail` 3, e.g. right after the double click that opened it in an
            // editor) closes (review 0048/02), and so do keyboard-made clicks
            // (`detail` 0, unaffected by any of this).
            if (event.detail === 2) {
                return;
            }
            close();
        };
        const onKeyDown = (event: Event): void => {
            if (!popover?.isConnected) {
                teardown();
                return;
            }
            const pressed = (event as KeyboardEvent).key;
            if (pressed === "Tab") {
                // Focus trap: nothing below the overlay may receive the focus.
                event.preventDefault();
                return;
            }
            if (CLOSING_KEYS.has(pressed)) {
                // Not for whatever the key would do below: the preview has the focus.
                event.preventDefault();
                close();
            }
        };

        popover = env.popover(html`
            <div class="img-preview" title="Klicken, um zu schließen" @click=${onClick}>
                <img src=${src} alt=${alt}>
            </div>
        `, {"full-size-preview": true});
        if (!popover) {
            return;
        }

        restoreFocus = env.focusPreview(popover, alt ? `Bildvorschau: ${alt}` : "Bildvorschau");
        env.keyTarget.addEventListener("keydown", onKeyDown);
        observer = env.observe(() => {
            if (!popover?.isConnected) {
                teardown();
                return;
            }
            if (!owner.isConnected) {
                close();
            }
        });
        ImagePreviewHelper.teardownActive = teardown;
    }

}
